import React, {FC} from 'react';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelModel} from '../../../../../../../libs/shared/core/src/models/api/channelModel';
import {
  LoadedMessageModel,
  MessageVisualStatusesEnum,
} from '../../../../../../../libs/shared/core/src/models/api/messageModel';
import {
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {PermissionCheck} from '../../../../../../../srcCommon/permissions/permissionCheck';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {AnnotationsHighlightViewerInheritableProps} from '../../../../../common/annotation/annotationsHighlightViewer';
import {MessageViewerActionReply} from '../action/messageViewerActionReply';
import {MessageViewerActionZone} from '../action/messageViewerActionZone';
import {MessageViewerFullHeader, MessageViewerFullHeaderProps} from '../messageViewerFullHeader';
import {MessageViewerHeaderButton} from '../messageViewerHeaderButton';
import {MessageViewerTweetBody, MessageViewerTweetBodyProps} from './messageViewerTweetBody';
import {
  MessageViewerTweetRetweetButton,
  MessageViewerTweetRetweetButtonProps,
} from './messageViewerTweetRetweetButton';

/*
 * Props.
 */

interface MessageViewerTweetProps
  extends MessageViewerFullHeaderProps,
    MessageViewerTweetRetweetButtonProps,
    MessageViewerTweetBodyProps,
    AnnotationsHighlightViewerInheritableProps {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
  namespace: string;
  message: LoadedMessageModel;
  replyChannel?: ChannelModel;
  isLastMessage?: boolean;
  onToggleLikeClick: () => void;
  isInMessagePopup: boolean;
}

/*
 * Component.
 */

export const MessageViewerTweet: FC<MessageViewerTweetProps> = (props) => (
  <>
    {renderHeader(props)}
    <MessageViewerTweetBody {...props} />
    {maybeRenderActions(props)}
  </>
);

/*
 * Header.
 */

function renderHeader(props: MessageViewerTweetProps) {
  return (
    <MessageViewerFullHeader {...props} onCollapseClick={() => {}}>
      <PermissionCheck namespaces={[props.namespace]} permission={PermissionsEnum.CONVERSATION_MESSAGE}>
        {(permission) => (
          <>
            {renderLikeButton(props, permission.isAllowed)}
            <MessageViewerTweetRetweetButton {...props} isDisabled={!permission.isAllowed} />
          </>
        )}
      </PermissionCheck>
    </MessageViewerFullHeader>
  );
}

function renderLikeButton(props: MessageViewerTweetProps, hasPermission: boolean) {
  const {message, onToggleLikeClick} = props;
  const isLiked =
    message.visualStatus === MessageVisualStatusesEnum.TIMELINE &&
    message.twitterExtra &&
    message.twitterExtra.isLiked;

  if (isLiked) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <MessageViewerHeaderButton
            iconName="likeFilled"
            color={theme.palette.red.shade40}
            label={<Trans id="message-tweet-unlike">Unlike</Trans>}
            hoverColor={theme.palette.red.shade50}
            onClick={onToggleLikeClick}
            isDisabled={!hasPermission}
          />
        )}
      </UserThemeConsumer>
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <MessageViewerHeaderButton
          iconName="like"
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
          label={<Trans id="message-tweet-like">Like</Trans>}
          onClick={onToggleLikeClick}
          isDisabled={!hasPermission}
        />
      )}
    </UserThemeConsumer>
  );
}

/*
 * Actions.
 */

function maybeRenderActions(props: MessageViewerTweetProps) {
  if (!props.replyChannel || !props.isLastMessage) {
    return null;
  }

  // You are always allowed to compose a reply, not necessarily to send it.
  return (
    <MessageViewerActionZone isInPopup={props.isInMessagePopup}>
      <MessageViewerActionReply
        iconName="reply"
        onClick={props.onReplyClick}
        messageType={props.message.type}
      />
    </MessageViewerActionZone>
  );
}
